
export interface VisualLoggerProps {
    maxLines: number;
    pos: 'TopLeft' | 'TopRight' | 'BottomLeft' | 'BottomRight';
    props: any;
}

/**
 * Component that provides visual loggin inside the provided container.
 */
export default class VisualLogger {

    /**
     * The HTML element for the visual logger
     */
    private _canvas!: HTMLElement;
    /**
     * The HTML element for the visual logger
     */
    public get canvas(): HTMLElement {
        return this._canvas;
    }
    public set canvas(v: HTMLElement) {
        this._canvas = v;
    }

    /**
     * The max number of lines in the logger.
     */
    private _maxLines: number = 5;
    /**
     * The max number of lines in the logger.
     */
    public get maxLines(): number {
        return this._maxLines;
    }
    public set maxLines(v: number) {
        this._maxLines = v;
    }

    /**
     * The data to be displayed in the logger
     * @property {string} text The string to be displayed
     * @property {number | NodeJS.Timeout} timeout The timeout id of the clearing timeout.
     * @property {string} style The css style to be applied
     */
    private _data: { text: string, timeout: NodeJS.Timeout, style: string }[] = [];
    /**
     * The data to be displayed in the logger
     * @property {string} text The string to be displayed
     * @property {NodeJS.Timeout} timeout The timeout id of the clearing timeout.
     * @property {string} style The css style to be applied
     */
    public get data(): { text: string, timeout: NodeJS.Timeout, style: string }[] {
        return this._data;
    }
    public set data(v: { text: string, timeout: NodeJS.Timeout, style: string }[]) {
        this._data = v;
    }

    /**
    * The time in seconds that text will be displayed in the logger
    */
    private _renderTime: number = 5;
    /**
     * The time in seconds that text will be displayed in the logger
     */
    public get renderTime(): number {
        return this._renderTime;
    }
    public set renderTime(v: number) {
        this._renderTime = v;
    }

    private _targetEnvironment: 'development' | 'production' = "development";
    public get targetEnvironment(): 'development' | 'production' {
        return this._targetEnvironment;
    }
    public set targetEnvironment(v: 'development' | 'production') {
        this._targetEnvironment = v;
    }

    /**
     * Creates a logger that will print text inside the given html element
     * @param container The element that will contain this logger
     * @param options Desired options for the visual logger
     */
    constructor(container: HTMLElement, options?: Partial<VisualLoggerProps>) {
        let { maxLines, pos, props } = options ?? {};
        this.maxLines = maxLines ?? 10;

        this.canvas = document.getElementById('VisualLogger') ?? document.createElement('div');
        this.canvas.id = 'VisualLogger';
        this.canvas.style.position = 'absolute';
        this.canvas.style.padding = '16px';
        switch (pos) {
            case 'BottomLeft': {
                this.canvas.style.bottom = '0';
                this.canvas.style.left = '0';
                break;
            }

            case 'BottomRight': {
                this.canvas.style.bottom = '0';
                this.canvas.style.right = '0';
                this.canvas.style.textAlign = 'end';
                break;
            }
            case 'TopLeft': {
                this.canvas.style.top = '0';
                this.canvas.style.left = '0';
                break;
            }
            default:
            case 'TopRight': {
                this.canvas.style.top = '0';
                this.canvas.style.right = '0';
                this.canvas.style.textAlign = 'end';
                break;
            }
        }
        if (props) {
            Object.entries(props).forEach(([key, value]) => {
                this.canvas[key] = value;
            });
        }
        var style = window.getComputedStyle(this.canvas, null).getPropertyValue('font-size');
        var fontSize = parseFloat(style);
        this.canvas.style.height = `${fontSize * this.maxLines}`;
        container.appendChild(this.canvas);
    }

    log(data: string | any, style?: string) {
        if (process.env.REACT_APP_UI_ENVIRONMENT && ![this.targetEnvironment, 'development'].includes(process.env.REACT_APP_UI_ENVIRONMENT))
            return;
        let string;
        if (typeof data === 'string') {
            string = data;
        }
        else {
            string = JSON.stringify(data);
        }
        if (this.data.length > this.maxLines) {
            let overflow = this.data.length - this.maxLines;
            for (let index = 0; index < overflow; index++) {
                const timeoutId = this.data[0].timeout;
                clearTimeout(timeoutId);
                this.data.splice(0, 1);
            }
        }

        let renderTimout = setTimeout(() => {
            this.data?.splice(0, 1);
            this?.render();
        }, this._renderTime * 1000);
        this.data.push({ text: string, timeout: renderTimout, style: style ?? '' });
        this.render();
    }

    render() {
        this.canvas.innerHTML = this.data.reduce((accu, line) => {
            accu += `<span style='${line.style}'>${line.text}</span><br>`;
            return accu;
        }, '')
    }

    cleanup() {
        this.data.forEach(entry => {
            clearTimeout(entry.timeout);
        });
        this.data = []
    }
}